// Supervisor monitor view at /live/calls/{callId} (UX-SPEC 6.7, COACH-01..04): the call summary and Listen / Whisper /
// Stop, gated by the coaching.live capability and the coaching:live permission, with honest states. The call comes from
// the workspace cache (kept current by the event stream) and is re-read every 5 seconds while the page is open.
import type { Call, Capability } from '@bcio-voice/contracts';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { CoachingClient } from '../../lib/api.ts';
import { capability } from '../../lib/capabilities.ts';
import { formatPhone } from '../../lib/format.ts';
import { useOptionalRouter } from '../../lib/router.tsx';
import { useWorkspace } from '../../state/workspace.tsx';
import { StatePanel } from '../../ui/StatePanel.tsx';
import { type CoachControls, coachControls } from './coaching.ts';
import { CoachControlGroup, type CoachMessage, CoachMessageLine } from './CoachControls.tsx';
import { CoachIcon } from './CoachIcons.tsx';
import { type CoachActionName, acceptedText, callSummary, runCoachCommand } from './monitor.ts';

export const MONITOR_REFRESH_MS = 5000;

export type MonitorViewProps = {
  callId: string;
  call: Call | null;
  loading: boolean;
  capability: Capability | null;
  controls: CoachControls | null;
  summary: { label: string; value: string }[];
  busy: CoachActionName | null;
  message: CoachMessage | null;
  onAction: (action: CoachActionName) => void;
};

/** Pure view: every state is decided by the props (tested by server rendering). */
export function MonitorView({ callId, call, loading, capability: cap, controls, summary, busy, message, onAction }: MonitorViewProps) {
  const header = (
    <>
      <p className="card-kicker"><BackLink /></p>
      <h1 className="page-title">Live call</h1>
    </>
  );
  if (cap && !cap.allowed) {
    return <div className="page coach-monitor">{header}<StatePanel variant="unavailable" title="Live coaching isn't part of your role" body="Ask an owner or admin for the coaching permission to listen to or whisper on live calls." /></div>;
  }
  if (!call) {
    return (
      <div className="page coach-monitor">
        {header}
        {loading ? <StatePanel variant="loading" title="Loading the call…" /> : <StatePanel variant="empty" title="This call isn't available" body="It may have ended, or it isn't one you can see." />}
      </div>
    );
  }
  const ended = call.state === 'ended' || call.state === 'failed';
  return (
    <div className="page coach-monitor" data-call-id={callId}>
      {header}
      <section aria-labelledby="coach-summary-title" className="coach-summary">
        <h2 id="coach-summary-title" className="section-title">Call summary</h2>
        <dl className="kv">
          {summary.map(row => (
            <div key={row.label} className="kv-row"><dt>{row.label}</dt><dd>{row.value}</dd></div>
          ))}
        </dl>
      </section>
      <section aria-labelledby="coach-controls-title" className="coach-panel">
        <h2 id="coach-controls-title" className="section-title"><CoachIcon name="listen" /> Coaching</h2>
        {ended ? <p className="inline-reason">This call has ended.</p> : controls ? <CoachControlGroup id="coach-monitor" controls={controls} busy={busy} onAction={onAction} /> : null}
        <CoachMessageLine message={message} />
      </section>
    </div>
  );
}

function BackLink() {
  const router = useOptionalRouter();
  return (
    <a href="/analytics" onClick={event => { if (!router || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return; event.preventDefault(); router.navigate('/analytics'); }}>
      <CoachIcon name="back" /> Back to analytics
    </a>
  );
}

/** Wired page: workspace call cache, capability, this device's readiness, and the coaching command client. */
export function CoachingMonitorPage({ callId }: { callId: string }) {
  const ws = useWorkspace();
  const client = useMemo(() => new CoachingClient(ws.api), [ws.api]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<CoachActionName | null>(null);
  const [message, setMessage] = useState<CoachMessage | null>(null);
  const call = ws.calls.get(callId) ?? null;
  const refetch = ws.refetchCall;
  useEffect(() => {
    let live = true;
    void refetch(callId).finally(() => { if (live) setLoading(false); });
    const timer = setInterval(() => { if (typeof document === 'undefined' || document.visibilityState !== 'hidden') void refetch(callId); }, MONITOR_REFRESH_MS);
    return () => { live = false; clearInterval(timer); };
  }, [callId, refetch]);
  const cap = capability(ws.config.value, 'coaching.live');
  const deviceReady = ws.readiness.state === 'ready' && !!ws.device && !ws.device.revoked_at;
  const controls = call ? coachControls({ capability: cap ?? undefined, userId: ws.userId, call, deviceReady, pending: busy !== null }) : null;
  const summary = call ? callSummary(call, ws.userId, id => ws.userName(id), formatPhone) : [];
  const onAction = useCallback(async (action: CoachActionName) => {
    if (busy) return;
    setBusy(action);
    setMessage(null);
    const joined = call?.coaching?.coach_user_id === ws.userId && call.coaching.state === 'active';
    const outcome = await runCoachCommand(client, callId, action, ws.device?.id ?? null);
    setMessage(outcome.ok ? { tone: 'info', text: acceptedText(action, joined), requestId: null } : { tone: 'failed', text: outcome.text, requestId: outcome.requestId });
    await refetch(callId);
    setBusy(null);
  }, [busy, call, client, callId, refetch, ws.device, ws.userId]);
  return <MonitorView callId={callId} call={call} loading={loading} capability={cap} controls={controls} summary={summary} busy={busy} message={message} onAction={a => void onAction(a)} />;
}
